import { cx } from "@/lib/tailwind-utils";

/** Which edge the bubble hugs. */
export type BubbleSide = "left" | "right";
/** Bubble fill. Defaults per side, but the portal inverts them. */
export type BubbleTone = "primary" | "surface" | "card";

interface ChatBubbleProps {
  side: BubbleSide;
  tone?: BubbleTone;
  size?: "sm" | "md" | "lg";
  /** Rounded corner on the tail side, pointing at the sender. */
  tail?: boolean;
  highlighted?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const TONES: Record<BubbleTone, string> = {
  // `[&_*]:text-primary-foreground` wins over descendant colours on purpose:
  // badges and tiles inside this bubble must be tinted against the fill.
  primary: "bg-primary text-primary-foreground [&_*]:text-primary-foreground",
  surface: "bg-background-secondary backdrop-blur-sm text-foreground",
  card: "bg-card text-foreground border border-border",
};

export function ChatBubble({
  side,
  tone,
  size = "lg",
  tail = true,
  highlighted,
  children,
  className,
  onClick,
}: ChatBubbleProps) {
  const resolvedTone = tone ?? (side === "left" ? "primary" : "surface");

  return (
    <div
      className={cx(
        "w-max break-words rounded-2xl",
        size === "sm" && "max-w-[70%] min-w-[3rem] p-2 px-3 text-caption",
        size === "md" && "max-w-[80%] px-4 py-2.5 text-body",
        size === "lg" &&
          "max-w-[min(70%,500px)] min-w-[4rem] p-3 px-4 text-body",
        size !== "md" && "shadow-md",
        side === "left" ? "mr-auto" : "ml-auto",
        tail && (side === "left" ? "rounded-bl-md" : "rounded-br-md"),
        TONES[resolvedTone],
        highlighted &&
          "ring-2 ring-primary ring-offset-2 ring-offset-background",
        onClick && "cursor-pointer transition-shadow hover:shadow-md",
        className,
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}
